import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import Typography from "@mui/material/Typography";
import { SNETFileUpload, AlertBox, alertTypes } from "shared/dist/components";
import isEmpty from "lodash/isEmpty";
import { aiServiceDetailsActions, loaderActions } from "../../../Services/Redux/actionCreators";
import { LoaderContent } from "../../../Utils/Loader";
import JSZip from "jszip";
import { isIndexFileInsideFolder } from "../../../Utils/ValidateCompressedFiles";
import { handleStatusTypes, messagesAlerts } from "./DemoFilesStatus";
import { assetTypes, fileTypes } from "../../../Utils/FileUpload";
import classes from "./Demo.module.css";
import { updateProgressStatus } from "../../../Services/Redux/actionCreators/aiServiceDetailsActions";
import { sections } from "../constant";

let isWaitingChangeDemoStatus = false;
let isDemoFilesUploading = false;
const waitingChangeDemoStatusTimeout = 1000;

const UploadDemoFiles = ({ selectedFile, uploadDemoFilesDetails }) => {
    const { orgUuid, serviceUuid } = useParams();
    const progressStages = useSelector((state) => state.aiServiceDetails.progressStages);
    const demoFiles = useSelector((state) => state.aiServiceDetails.assets.demoFiles);
    const demoBuildIdRef = useRef(demoFiles.buildId);
    const [alert, setAlert] = useState();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!demoFiles.url) {
            setAlert({ type: alertTypes.WARNING, message: messagesAlerts.hasNotFiles });
        } else {
            setAlert();
        }
    }, [demoFiles.url]);

    const waitingChangeDemoBuildId = async (timeout) => {
        if (isWaitingChangeDemoStatus) {
            return;
        }
        while (demoBuildIdRef.current === demoFiles.buildId) {
            await new Promise((resolve) => setTimeout(resolve, timeout));
            demoBuildIdRef.current = await getDemoBuildId();
            isWaitingChangeDemoStatus = true;
        }
        dispatch(
            updateProgressStatus(sections.SETUP_DEMO, handleStatusTypes.PENDING, progressStages)
        );
        isWaitingChangeDemoStatus = false;
    };

    const getDemoBuildId = async () => {
        try {
            const newDemoBuildId = await dispatch(
                aiServiceDetailsActions.getHandlingFileBuildId(
                    orgUuid,
                    serviceUuid,
                    fileTypes.demoFiles
                )
            );
            return newDemoBuildId;
        } catch (error) {
            setAlert({
                type: alertTypes.ERROR,
                message: "Please, try again",
            });
        }
    };

    const modalTimeout = async () => {
        try {
            dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
            await waitingChangeDemoBuildId(waitingChangeDemoStatusTimeout);
        } catch (err) {
            console.error("upload UI error: ", err);
        } finally {
            dispatch(loaderActions.stopAppLoader());
        }
    };

    const handleFileDrop = async (acceptedFiles, rejectedFiles) => {
        if (isDemoFilesUploading) {
            return;
        }
        isDemoFilesUploading = true;
        if (!isEmpty(rejectedFiles)) {
            setAlert({
                type: alertTypes.ERROR,
                message: messagesAlerts.rejectedFile,
            });
            return;
        }
        if (isEmpty(acceptedFiles)) {
            return;
        }
        try {
            const fileBlob = acceptedFiles[0];
            await validateIndexFile(fileBlob);
            const { name, size, type } = fileBlob;
            uploadDemoFilesDetails({ name, size, type });
            dispatch(loaderActions.startAppLoader(LoaderContent.UPLOAD_FILE));
            const { url } = await dispatch(
                aiServiceDetailsActions.uploadFile(
                    assetTypes.SERVICE_PAGE_COMPONENTS,
                    fileBlob,
                    orgUuid,
                    serviceUuid
                )
            );
            if (!url) {
                throw new Error("Something went wrong...");
            }
            dispatch(aiServiceDetailsActions.setServiceDemoFilesUrl(url));
            dispatch(aiServiceDetailsActions.setServiceTouchedFlag(true));
            await modalTimeout(); // waiting update on back from s3
        } catch (e) {
            console.error("error in demo files uploading", e);
            setAlert({ type: alertTypes.ERROR, message: e.message });
        } finally {
            isDemoFilesUploading = false;
            dispatch(loaderActions.stopAppLoader());
        }
    };

    const validateIndexFile = async (uploadedFile) => {
        const fileToBePresent = "index.js";

        const zip = new JSZip();
        await zip.loadAsync(uploadedFile).then((entry) => {
            const indexFileInsideSomeFolder = isIndexFileInsideFolder(entry);

            if (indexFileInsideSomeFolder) {
                throw new Error(messagesAlerts.indexFileInsideFolder);
            } else {
                const indexFileFound = Object.values(entry.files).some((file) => {
                    return file.name === fileToBePresent;
                });

                if (!indexFileFound) {
                    throw new Error(messagesAlerts.hasNotIndexFile);
                }
            }
        });
    };

    const acceptedFileTypes = {
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
    };

    return (
        <div className={classes.stepThreeContainer}>
            <Typography variant="subtitle1" className={classes.stepsHeading}>
                Step 3: Upload the Demo Files
            </Typography>
            <Typography variant="subtitle2">
                Zip / Rar / compress all the project files and drop it here.{" "}
            </Typography>
            <SNETFileUpload
                onDrop={handleFileDrop}
                accept={acceptedFileTypes}
                multiple={false}
                showFileDetails
                fileName={selectedFile.name}
                fileSize={selectedFile.size}
                fileDownloadURL={demoFiles.url}
                uploadSuccess={Boolean(demoFiles.url)}
                error={alert?.type === alertTypes.ERROR ? alert : false}
                helperText={
                    <>
                        <Typography>
                            * Compress only the individual files with no parent folders
                        </Typography>
                        <Typography>* Package must be under 2mb</Typography>
                        <Typography>* Make sure the extension is .zip</Typography>
                    </>
                }
            />
            {alert && <AlertBox type={alert.type} message={alert.message} />}
        </div>
    );
};

export default UploadDemoFiles;
